import { Controller } from "@hotwired/stimulus"

// Drives the total session progress bar plus the per-slot highlight bar,
// the pause/resume button, and the auto-advance (or do-nothing) timing for
// each prayer session item.
//
// Values:
//   duration    (Number)  – ms for this prayer slot
//   autoRotate  (Boolean) – auto-click Next when slot expires; otherwise
//                           the Next button is always visible for manual use
//   totalSlots  (Number)  – total number of slots planned for the session
//   currentSlot (Number)  – 1-based index of the slot currently displayed
//
// Targets:
//   previousBar   – filled portion representing all prior slots
//   currentBar    – animating fill for the current slot
//   pauseIcon     – inner element whose HTML is swapped between pause/play SVGs
//   slotEndMarker – vertical tick marking where the current slot ends
export default class extends Controller {
  static values = {
    duration: Number,
    autoRotate: Boolean,
    totalSlots: Number,
    currentSlot: Number
  }

  static targets = ["previousBar", "currentBar", "pauseIcon", "slotEndMarker"]

  connect () {
    this.remaining = this.durationValue
    this.paused = false

    this.boundPause = this.pauseOnBlur.bind(this)
    this.boundResume = this.resumeOnFocus.bind(this)
    window.addEventListener("blur", this.boundPause)
    window.addEventListener("focus", this.boundResume)

    this.initProgressBar()
    this.startTimer()
    this.startTicker()
  }

  disconnect () {
    this.clearTimer()
    this.clearTicker()
    window.removeEventListener("blur", this.boundPause)
    window.removeEventListener("focus", this.boundResume)
  }

  // ── Public action ─────────────────────────────────────────────────────────

  togglePause () {
    if (this.paused) {
      this.resume()
    } else {
      this.pause()
    }
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  initProgressBar () {
    const total = this.totalSlotsValue
    if (!total) return

    const prevPct = (this.currentSlotValue - 1) / total * 100
    const endPct  = this.currentSlotValue / total * 100

    if (this.hasPreviousBarTarget) {
      this.previousBarTarget.style.width = `${prevPct}%`
      // On the first slot there are no prior slots; match the highlight colour.
      if (this.currentSlotValue === 1) {
        this.previousBarTarget.classList.remove("bg-secondary/40")
        this.previousBarTarget.classList.add("bg-secondary")
      }
    }
    if (this.hasCurrentBarTarget) {
      this.currentBarTarget.style.left = `${prevPct}%`
      this.slotWidthPct = 100 / total
      this.currentBarTarget.style.width = "0%"
    }

    if (this.hasSlotEndMarkerTarget) {
      if (endPct >= 100) {
        this.slotEndMarkerTarget.classList.add("hidden")
      } else {
        this.slotEndMarkerTarget.style.left = `${endPct}%`
      }
    }
  }

  startTimer () {
    this.startedAt = Date.now()
    this.timer = setTimeout(() => this.advance(), this.remaining)
  }

  clearTimer () {
    clearTimeout(this.timer)
    this.timer = null
  }

  startTicker () {
    this.ticker = setInterval(() => this.tick(), 100)
  }

  clearTicker () {
    clearInterval(this.ticker)
    this.ticker = null
  }

  tick () {
    if (this.paused) return
    const elapsed = this.durationValue - this.remaining + (Date.now() - this.startedAt)
    const pct = Math.min(elapsed / this.durationValue, 1) * (this.slotWidthPct || 0)
    if (this.hasCurrentBarTarget) {
      this.currentBarTarget.style.width = `${pct}%`
    }
  }

  pause () {
    if (this.paused) return
    this.remaining -= Date.now() - this.startedAt
    this.clearTimer()
    this.paused = true
    this.updatePauseIcon()
  }

  resume () {
    if (!this.paused) return
    this.paused = false
    this.updatePauseIcon()
    if (this.remaining > 0) this.startTimer()
  }

  pauseOnBlur () { this.pause() }
  resumeOnFocus () { this.resume() }

  advance () {
    if (this.autoRotateValue) {
      const button = this.element.querySelector('button[type="submit"]')
      if (button) button.click()
    }
    // In manual mode the Next button is always visible; nothing extra to do.
  }

  updatePauseIcon () {
    if (!this.hasPauseIconTarget) return
    if (this.paused) {
      this.pauseIconTarget.innerHTML = this.playIconSvg()
    } else {
      this.pauseIconTarget.innerHTML = this.pauseIconSvg()
    }
  }

  pauseIconSvg () {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
      viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
    </svg>`
  }

  playIconSvg () {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
      viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>`
  }
}
